import React from "react";
import * as Yup from "yup";
import { useAdminApiClient } from "../hooks/useApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { FormikErrors, FormikProvider, useFormik } from "formik";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import { Button, Text } from "@chakra-ui/react";
import { CopyText } from "../components/Utilities/CopyText";
import { TextField } from "../components/fields/TextField/TextField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { JsonTextAreaField } from "../components/fields/TextArea/JsonTextAreaField";
import { HtmlTextArea } from "../components/fields/TextArea/HtmlTextArea";
import { ApiResult } from "../api/dataApiClient";
import { CopyFormButton } from "../components/CopyForm/CopyFormButton";
import { PasteFormButton } from "../components/CopyForm/PasteFormButton";

interface LandingPageConfigurationFormProps {
  businessId: string;
  landingPageConfiguration?: CherryHubAdminApi.LandingPageConfigurationItem | null;
  onCancel: () => void;
  onSuccess: (
    landingPageConfiguration: CherryHubAdminApi.LandingPageConfigurationItem
  ) => void;
  onFailure: (message?: string) => void;
}

export const LandingPageConfigurationForm = ({
  businessId,
  landingPageConfiguration,
  onCancel,
  onSuccess,
  onFailure,
}: LandingPageConfigurationFormProps) => {
  const apiClient = useAdminApiClient();

  const formik = useFormik<CherryHubAdminApi.LandingPageConfigurationUpdate>({
    initialValues: (landingPageConfiguration && {
      ...landingPageConfiguration,
      ThemeData: !landingPageConfiguration?.ThemeData
        ? ""
        : JSON.stringify(landingPageConfiguration?.ThemeData, null, 2),
    }) ?? {
      IsEnabled: true,
    },
    onSubmit: async (val) => {
      if (val.ThemeData) {
        val.ThemeData = JSON.parse(val.ThemeData as string);
      }

      let result: ApiResult<CherryHubAdminApi.LandingPageConfigurationItem> | null =
        null;

      if (landingPageConfiguration) {
        result = await apiClient.editLandingPageConfiguration(
          businessId!,
          landingPageConfiguration!.Meta!.Id!,
          val as CherryHubAdminApi.LandingPageConfigurationUpdate
        );
      } else {
        result = await apiClient.addLandingPageConfiguration(
          businessId!,
          val as CherryHubAdminApi.LandingPageConfigurationUpdate
        );
      }

      if (result.ok) {
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        if (onFailure) {
          onFailure(result.message);
        }
      }
    },
    validate: (val) => {
      const errors: FormikErrors<CherryHubAdminApi.LandingPageConfigurationUpdate> =
        {};

      try {
        if (val.ThemeData) {
          JSON.parse(val.ThemeData as string);
        }
      } catch (e) {
        errors.ThemeData = "Invalid json format";
      }
      return errors;
    },
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      FriendlyName: Yup.string().required("Friendly Name is required"),
      PageTitle: Yup.string().required("Page Title is required"),
      RedirectUrl: Yup.string()
        .url("Redirect URL must be a valid URL")
        .nullable()
        .notRequired(),
      PageHtmlContent: Yup.string()
        .required("Page HTML Content is required")
        .test("is-not-empty", "Page HTML Content is required", (value) => {
          // Remove HTML tags and check if the content is not empty
          const strippedContent = value?.replace(/<[^>]*>/g, "").trim();
          return strippedContent !== "";
        }),
    }),
  });

  return (
    <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
      <FormikProvider value={formik}>
        {landingPageConfiguration && (
          <HStack w="100%">
            <Box flexGrow={2} pb={4}>
              <CopyText
                text={landingPageConfiguration?.Meta?.Id!}
                hover
                adjustMargin
              >
                <Text fontSize="md" color="cherryUi.600">
                  {landingPageConfiguration?.Meta?.Id}
                </Text>
              </CopyText>
              <Text fontSize="xl" color="cherryUi.600" fontWeight="bold">
                {landingPageConfiguration?.FriendlyName}
              </Text>
            </Box>

            <Box>
              <Button
                colorScheme="cherryButton"
                type="submit"
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                Save
              </Button>
            </Box>

            <Box>
              <CopyFormButton<CherryHubAdminApi.LandingPageConfigurationUpdate>
                hooksProps={{
                  key: "LandingPageConfiguration",
                  setValues: formik.setValues,
                }}
                values={formik.values}
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              />
            </Box>

            <Box>
              <Button disabled={formik.isSubmitting} onClick={onCancel}>
                Cancel
              </Button>
            </Box>
          </HStack>
        )}

        {!landingPageConfiguration && (
          <HStack w="100%">
            <Box ml="auto" pb={4}>
              <PasteFormButton<CherryHubAdminApi.LandingPageConfigurationUpdate>
                hooksProps={{
                  key: "LandingPageConfiguration",
                  setValues: formik.setValues,
                }}
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting}
              />
            </Box>
          </HStack>
        )}

        <VStack
          w="100%"
          alignItems="flex-start"
          maxW="1200px"
          pb={10}
          spacing={4}
        >
          <TextField horizontal label="Friendly Name" name="FriendlyName" />

          <SwitchField horizontal label="Enabled" name="IsEnabled" />

          <Box w="100%">
            <TextField horizontal label="Redirect URL" name="RedirectUrl" />
            <Text fontSize="xs" color="cherryGrey.600" mt={1} ml="25%">
              The redirect URL only takes effect when the form is disabled
            </Text>
          </Box>

          <TextField horizontal label="Page Title" name="PageTitle" />

          <TextField horizontal label="Logo URL" name="LogoFileUrl" />

          <TextField
            horizontal
            label="Privacy Policy URL"
            name="PrivacyPolicyUrl"
          />

          <TextField
            horizontal
            label="Terms & Conditions URL"
            name="TermsConditionsUrl"
          />

          <HtmlTextArea
            horizontal
            label="Page HTML Content"
            name="PageHtmlContent"
          />

          <VStack w="100%" spacing={4} alignItems="flex-start">
            <JsonTextAreaField
              horizontal
              label="Theme Data"
              name="ThemeData"
              valueText={formik.values.ThemeData}
              errors={formik.errors.ThemeData}
              setFieldValue={formik.setFieldValue}
              rows={10}
            />
          </VStack>
        </VStack>

        <HStack width="100%" justifyContent="center" spacing="3" pt="8">
          <Button
            type="submit"
            colorScheme="cherryButton"
            isDisabled={formik.isSubmitting || !formik.isValid}
          >
            Save
          </Button>
          <Button
            ml={5}
            isDisabled={formik.isSubmitting}
            onClick={() => onCancel()}
          >
            Cancel
          </Button>
        </HStack>
      </FormikProvider>
    </form>
  );
};
